import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserCircle, Plus, PencilSimple, Trash, MapPin, Paperclip, UploadSimple, DownloadSimple, X, PaperPlaneTilt, Storefront, Eye } from "@phosphor-icons/react";
import { IDENTITY_MODES, ORG_VISIBILITY, AVAILABILITY, REMOTE_PREFS, availabilityMeta, rateRange } from "@/lib/marketplace";

const SENIORITY = ["Junior", "Confirmé", "Senior", "Expert"];
const AVAIL = [
  { v: "available", label: "Disponible", color: "#00E5FF" },
  { v: "soon", label: "Bientôt disponible", color: "#F59E0B" },
  { v: "unavailable", label: "En mission", color: "#8A8A93" },
];
const inputCls = "bg-surface-2 border-border text-fg";

function availMeta(v) { return AVAIL.find((a) => a.v === v) || AVAIL[0]; }

export default function Consultants() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const maxUsers = user?.entitlements?.max_users || 3;
  const canPublish = !!user?.entitlements?.marketplace_publish;

  // ---- Marketplace publication state ----
  const [mkOpen, setMkOpen] = useState(false);
  const [mkConsultant, setMkConsultant] = useState(null);
  const [mkForm, setMkForm] = useState({});
  const [mkPreview, setMkPreview] = useState(null);
  const [mkBusy, setMkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/consultants");
      setItems(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", role: "", seniority: "Confirmé", skills: "", location: "", availability: "available",
      daily_rate: "", years_experience: "", mobility: "", linkedin: "", summary: "", status: "active" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...c, skills: (c.skills || []).join(", ") });
    setOpen(true);
  };
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const uploadCv = async (c, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/crm/consultants/${c.id}/cv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((x) => x.map((y) => y.id === c.id ? { ...y, cv_file_id: data.cv_file_id, cv_filename: data.cv_filename } : y));
      toast.success("CV enregistré");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeCv = async (c) => {
    try {
      await api.delete(`/crm/consultants/${c.id}/cv`);
      setItems((x) => x.map((y) => y.id === c.id ? { ...y, cv_file_id: null, cv_filename: null } : y));
      toast.success("CV retiré");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const downloadCv = async (c) => {
    try {
      const { data } = await api.get(`/crm/consultants/${c.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Impossible d'ouvrir le CV"); }
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Le nom du consultant est requis."); return; }
    try {
      if (editing) await api.patch(`/consultants/${editing.id}`, form);
      else await api.post("/consultants", form);
      toast.success(editing ? "Consultant modifié" : "Consultant ajouté");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/consultants/${id}`);
      setItems((x) => x.filter((c) => c.id !== id));
      toast.success("Consultant supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---- Marketplace: publication + confidentialité ----
  const openMarketplace = async (c) => {
    setMkConsultant(c);
    setMkForm({
      marketplace_identity_mode: c.marketplace_identity_mode || "ANONYMOUS",
      marketplace_org_visibility: c.marketplace_org_visibility || "SHOW_ORGANIZATION",
      availability_status: c.availability_status || (c.availability === "available" ? "AVAILABLE_NOW" : "NOT_AVAILABLE"),
      remote_preference: c.remote_preference || "UNKNOWN",
      headline: c.headline || c.role || "",
      marketplace_summary: c.marketplace_summary || c.summary || "",
      daily_rate_min: c.daily_rate_min || "",
      daily_rate_max: c.daily_rate_max || "",
      languages: Array.isArray(c.languages) ? c.languages.join(", ") : (c.languages || ""),
    });
    setMkPreview(null);
    setMkOpen(true);
    try {
      const { data } = await api.get(`/consultants/${c.id}/marketplace/preview`);
      setMkPreview(data);
    } catch { /* ignore */ }
  };
  const mkUpd = (k) => (e) => setMkForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const refreshPreview = async (id) => {
    try {
      const { data } = await api.get(`/consultants/${id}/marketplace/preview`);
      setMkPreview(data);
    } catch { /* ignore */ }
  };

  const saveMkSettings = async () => {
    if (!mkConsultant) return;
    setMkBusy(true);
    try {
      const payload = { ...mkForm };
      if (payload.daily_rate_min === "") delete payload.daily_rate_min; else payload.daily_rate_min = Number(payload.daily_rate_min);
      if (payload.daily_rate_max === "") delete payload.daily_rate_max; else payload.daily_rate_max = Number(payload.daily_rate_max);
      const { data } = await api.patch(`/consultants/${mkConsultant.id}/marketplace`, payload);
      setItems((x) => x.map((y) => y.id === data.id ? data : y));
      setMkConsultant(data);
      await refreshPreview(data.id);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMkBusy(false); }
  };

  const togglePublish = async () => {
    if (!mkConsultant) return;
    const publishing = !mkConsultant.marketplace_visible;
    setMkBusy(true);
    try {
      // On enregistre d'abord les réglages puis on publie/retire.
      if (publishing) {
        const payload = { ...mkForm };
        if (payload.daily_rate_min === "") delete payload.daily_rate_min; else payload.daily_rate_min = Number(payload.daily_rate_min);
        if (payload.daily_rate_max === "") delete payload.daily_rate_max; else payload.daily_rate_max = Number(payload.daily_rate_max);
        await api.patch(`/consultants/${mkConsultant.id}/marketplace`, payload);
      }
      const url = publishing ? `/consultants/${mkConsultant.id}/publish` : `/consultants/${mkConsultant.id}/unpublish`;
      const { data } = await api.post(url);
      const updated = publishing ? data : { ...mkConsultant, marketplace_visible: false };
      setItems((x) => x.map((y) => y.id === mkConsultant.id ? { ...y, ...updated } : y));
      setMkConsultant((c) => ({ ...c, ...updated }));
      toast.success(publishing ? "Consultant publié dans la Marketplace" : "Consultant retiré de la Marketplace");
      await refreshPreview(mkConsultant.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMkBusy(false); }
  };

  return (
    <div data-testid="consultants-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Votre vivier</div>
          <h1 className="font-display text-3xl font-bold">Consultants disponibles <span className="text-muted text-lg font-mono">({items.length})</span></h1>
          <p className="text-muted text-sm mt-1">Gérez vos consultants en intercontrat et positionnez-les sur les opportunités détectées.</p>
        </div>
        <button onClick={openCreate} data-testid="consultant-add-btn"
          className="flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift shrink-0">
          <Plus size={16} weight="bold" /> Ajouter un consultant
        </button>
      </div>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-12 text-center" data-testid="consultants-empty">
          <UserCircle size={40} weight="duotone" className="text-accent mx-auto mb-3" />
          <div className="font-display font-bold text-fg mb-1">Aucun consultant enregistré</div>
          <p className="text-muted text-sm mb-5">Ajoutez vos consultants disponibles pour les positionner sur les bonnes opportunités.</p>
          <button onClick={openCreate} className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift">Ajouter mon premier consultant</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="consultants-grid">
          {items.map((c) => {
            const a = availMeta(c.availability);
            return (
              <div key={c.id} className="surface rounded-lg p-5 hover-lift" data-testid={`consultant-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-fg truncate">{c.name}</h3>
                    <div className="text-sm text-muted truncate">{c.role || "—"} · {c.seniority}</div>
                  </div>
                  <span className="shrink-0 flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-full border" style={{ color: a.color, borderColor: a.color + "55", background: a.color + "14" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: a.color }} /> {a.label}
                  </span>
                </div>
                {c.location && <div className="flex items-center gap-1 text-xs text-muted mt-2"><MapPin size={12} /> {c.location}</div>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(c.skills || []).slice(0, 6).map((s) => (
                    <span key={s} className="text-xs font-mono bg-surface-2 text-fg/80 border border-border px-2 py-0.5 rounded">{s}</span>
                  ))}
                </div>
                {(c.daily_rate || c.years_experience) && (
                  <div className="flex items-center gap-3 text-xs text-muted mt-2">
                    {c.years_experience ? <span>{c.years_experience} ans d'exp.</span> : null}
                    {c.daily_rate ? <span>TJM {c.daily_rate}€</span> : null}
                  </div>
                )}
                {/* CV management */}
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`consultant-cv-${c.id}`}>
                  {c.cv_file_id ? (
                    <>
                      <button onClick={() => downloadCv(c)} data-testid={`consultant-cv-view-${c.id}`}
                        className="flex items-center gap-1.5 text-xs bg-accent/10 text-accent border border-accent/25 rounded px-2 py-1 hover:bg-accent/15">
                        <Paperclip size={12} /> <span className="truncate max-w-[140px]">{c.cv_filename || "CV"}</span> <DownloadSimple size={12} />
                      </button>
                      <button onClick={() => removeCv(c)} title="Retirer le CV" data-testid={`consultant-cv-remove-${c.id}`}
                        className="p-1 text-muted hover:text-destructive"><X size={13} /></button>
                    </>
                  ) : (
                    <label className="flex items-center gap-1.5 text-xs text-muted hover:text-accent cursor-pointer" data-testid={`consultant-cv-upload-label-${c.id}`}>
                      <UploadSimple size={13} /> Ajouter un CV
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid={`consultant-cv-upload-${c.id}`}
                        onChange={(e) => uploadCv(c, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-4 pt-3 border-t border-border">
                  {canPublish && (
                    <button onClick={() => openMarketplace(c)} data-testid={`consultant-marketplace-${c.id}`}
                      className={`text-xs flex items-center gap-1 mr-auto rounded-full border px-2.5 py-1 hover-lift ${c.marketplace_visible ? "text-accent border-accent/40 bg-accent/10" : "text-muted border-border hover:text-accent"}`}>
                      <Storefront size={13} weight={c.marketplace_visible ? "fill" : "regular"} />
                      {c.marketplace_visible ? "Publié" : "Publier"}
                    </button>
                  )}
                  <Link to={`/app/crm/emails?consultant=${c.id}`} className={`text-xs text-muted hover:text-accent flex items-center gap-1 ${canPublish ? "" : "mr-auto"}`} data-testid={`consultant-positions-${c.id}`}>
                    <PaperPlaneTilt size={13} /> Envois
                  </Link>
                  <button onClick={() => openEdit(c)} className="p-2 rounded-md text-muted hover:text-accent hover:bg-surface-2" data-testid={`consultant-edit-${c.id}`}><PencilSimple size={15} /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2" data-testid={`consultant-delete-${c.id}`}><Trash size={15} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-surface border-border">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer {c.name} ?</AlertDialogTitle>
                        <AlertDialogDescription>Le consultant et ses positionnements seront supprimés.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-surface-2 border-border">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(c.id)} className="bg-destructive text-white" data-testid={`consultant-delete-confirm-${c.id}`}>Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border max-w-lg" data-testid="consultant-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier le consultant" : "Ajouter un consultant"}</DialogTitle>
            <DialogDescription className="text-muted">Décrivez le profil pour le positionner sur les opportunités.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="overline block mb-1.5">Nom</label>
              <Input className={inputCls} value={form.name || ""} onChange={upd("name")} placeholder="Marie Dupont" data-testid="consultant-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1.5">Poste / rôle</label>
                <Input className={inputCls} value={form.role || ""} onChange={upd("role")} placeholder="Ingénieur DevOps" data-testid="consultant-role" />
              </div>
              <div>
                <label className="overline block mb-1.5">Séniorité</label>
                <Select value={form.seniority} onValueChange={upd("seniority")}>
                  <SelectTrigger className={inputCls} data-testid="consultant-seniority"><SelectValue /></SelectTrigger>
                  <SelectContent>{SENIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="overline block mb-1.5">Compétences (séparées par des virgules)</label>
              <Input className={inputCls} value={form.skills || ""} onChange={upd("skills")} placeholder="AWS, Kubernetes, Terraform" data-testid="consultant-skills" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1.5">Localisation</label>
                <Input className={inputCls} value={form.location || ""} onChange={upd("location")} placeholder="Lille" data-testid="consultant-location" />
              </div>
              <div>
                <label className="overline block mb-1.5">Disponibilité</label>
                <Select value={form.availability} onValueChange={upd("availability")}>
                  <SelectTrigger className={inputCls} data-testid="consultant-availability"><SelectValue /></SelectTrigger>
                  <SelectContent>{AVAIL.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="overline block mb-1.5">TJM (€)</label>
                <Input className={inputCls} value={form.daily_rate || ""} onChange={upd("daily_rate")} placeholder="550" data-testid="consultant-daily-rate" />
              </div>
              <div>
                <label className="overline block mb-1.5">Années d'exp.</label>
                <Input className={inputCls} value={form.years_experience || ""} onChange={upd("years_experience")} placeholder="8" data-testid="consultant-experience" />
              </div>
              <div>
                <label className="overline block mb-1.5">Mobilité</label>
                <Input className={inputCls} value={form.mobility || ""} onChange={upd("mobility")} placeholder="France" data-testid="consultant-mobility" />
              </div>
            </div>
            <div>
              <label className="overline block mb-1.5">LinkedIn (optionnel)</label>
              <Input className={inputCls} value={form.linkedin || ""} onChange={upd("linkedin")} placeholder="https://linkedin.com/in/…" data-testid="consultant-linkedin" />
            </div>
            <div>
              <label className="overline block mb-1.5">Résumé du profil (optionnel)</label>
              <textarea className={`${inputCls} w-full rounded-md text-sm px-3 py-2 min-h-[70px] resize-y`} value={form.summary || ""}
                onChange={upd("summary")} placeholder="Consultant DevOps senior, 8 ans d'expérience AWS/Kubernetes…" data-testid="consultant-summary" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="consultant-save-btn" className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full">
              {editing ? "Enregistrer" : "Ajouter"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Marketplace — publication + confidentialité */}
      <Dialog open={mkOpen} onOpenChange={setMkOpen}>
        <DialogContent className="bg-surface border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="marketplace-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Storefront size={20} weight="duotone" className="text-accent" /> Marketplace — {mkConsultant?.name}</DialogTitle>
            <DialogDescription className="text-muted">Choisissez ce qui est visible publiquement. Email, téléphone, LinkedIn et TJM interne ne sont jamais exposés.</DialogDescription>
          </DialogHeader>

          {mkConsultant && (
            <div className="grid md:grid-cols-2 gap-5 py-1">
              {/* Réglages */}
              <div className="space-y-3">
                <div className={`rounded-md border px-3 py-2 text-sm flex items-center gap-2 ${mkConsultant.marketplace_visible ? "border-accent/40 bg-accent/10 text-accent" : "border-border bg-surface-2 text-muted"}`} data-testid="marketplace-status">
                  <span className={`w-2 h-2 rounded-full ${mkConsultant.marketplace_visible ? "bg-accent" : "bg-muted"}`} />
                  {mkConsultant.marketplace_visible ? "Publié dans la Marketplace" : "Non publié"}
                </div>

                <div>
                  <label className="overline block mb-1.5">Identité affichée</label>
                  <Select value={mkForm.marketplace_identity_mode} onValueChange={mkUpd("marketplace_identity_mode")}>
                    <SelectTrigger className={inputCls} data-testid="mk-identity-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{IDENTITY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline block mb-1.5">Visibilité de l'organisation</label>
                  <Select value={mkForm.marketplace_org_visibility} onValueChange={mkUpd("marketplace_org_visibility")}>
                    <SelectTrigger className={inputCls} data-testid="mk-org-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORG_VISIBILITY.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-1.5">Disponibilité</label>
                    <Select value={mkForm.availability_status} onValueChange={mkUpd("availability_status")}>
                      <SelectTrigger className={inputCls} data-testid="mk-availability"><SelectValue /></SelectTrigger>
                      <SelectContent>{AVAILABILITY.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Mode de travail</label>
                    <Select value={mkForm.remote_preference} onValueChange={mkUpd("remote_preference")}>
                      <SelectTrigger className={inputCls} data-testid="mk-remote"><SelectValue /></SelectTrigger>
                      <SelectContent>{REMOTE_PREFS.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="overline block mb-1.5">Titre public (headline)</label>
                  <Input className={inputCls} value={mkForm.headline || ""} onChange={mkUpd("headline")} placeholder="Ingénieur DevOps senior" data-testid="mk-headline" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-1.5">TJM public min (€)</label>
                    <Input className={inputCls} type="number" value={mkForm.daily_rate_min || ""} onChange={mkUpd("daily_rate_min")} placeholder="500" data-testid="mk-rate-min" />
                  </div>
                  <div>
                    <label className="overline block mb-1.5">TJM public max (€)</label>
                    <Input className={inputCls} type="number" value={mkForm.daily_rate_max || ""} onChange={mkUpd("daily_rate_max")} placeholder="650" data-testid="mk-rate-max" />
                  </div>
                </div>
                <div>
                  <label className="overline block mb-1.5">Langues (séparées par des virgules)</label>
                  <Input className={inputCls} value={mkForm.languages || ""} onChange={mkUpd("languages")} placeholder="Français, Anglais" data-testid="mk-languages" />
                </div>
                <div>
                  <label className="overline block mb-1.5">Résumé public</label>
                  <textarea className={`${inputCls} w-full rounded-md text-sm px-3 py-2 min-h-[70px] resize-y`} value={mkForm.marketplace_summary || ""}
                    onChange={mkUpd("marketplace_summary")} placeholder="Présentation anonyme du profil pour les acheteurs…" data-testid="mk-summary" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveMkSettings} disabled={mkBusy} data-testid="mk-save-btn"
                    className="flex-1 bg-surface-2 border border-border text-fg font-semibold py-2.5 rounded-md hover-lift disabled:opacity-60">Enregistrer</button>
                  <button onClick={togglePublish} disabled={mkBusy} data-testid="mk-publish-btn"
                    className={`flex-1 font-semibold py-2.5 rounded-md hover-lift disabled:opacity-60 ${mkConsultant.marketplace_visible ? "bg-destructive text-white" : "bg-accent text-black"}`}>
                    {mkConsultant.marketplace_visible ? "Retirer" : "Publier"}
                  </button>
                </div>
              </div>

              {/* Aperçu public */}
              <div className="rounded-lg border border-border bg-surface-2/40 p-4" data-testid="mk-preview">
                <div className="overline mb-3 flex items-center gap-1.5"><Eye size={13} className="text-accent" /> Aperçu acheteur</div>
                {mkPreview ? (
                  <div className="space-y-2">
                    <div className="font-display font-bold text-fg">{mkPreview.display_name}</div>
                    <div className="text-xs text-muted">{mkPreview.headline || mkPreview.role} · {mkPreview.seniority}</div>
                    {mkPreview.canonical_family_name && <span className="inline-block text-[11px] font-mono uppercase text-accent bg-accent/10 border border-accent/20 rounded px-2 py-0.5">{mkPreview.canonical_family_name}</span>}
                    <div className="flex flex-wrap gap-1.5">{(mkPreview.skills || []).slice(0, 8).map((s) => <span key={s} className="text-xs font-mono bg-surface text-fg/80 border border-border px-2 py-0.5 rounded">{s}</span>)}</div>
                    <div className="text-xs text-muted flex items-center gap-2 flex-wrap pt-1">
                      {(mkPreview.city || mkPreview.region) && <span className="flex items-center gap-1"><MapPin size={12} /> {mkPreview.city || mkPreview.region}</span>}
                      {rateRange(mkPreview.daily_rate_min, mkPreview.daily_rate_max, mkPreview.currency) && <span className="font-mono">{rateRange(mkPreview.daily_rate_min, mkPreview.daily_rate_max, mkPreview.currency)}</span>}
                    </div>
                    {mkPreview.summary && <p className="text-xs text-fg/80 whitespace-pre-line pt-1">{mkPreview.summary}</p>}
                    <div className="text-[11px] text-muted pt-2 border-t border-border">{mkPreview.organization_name ? mkPreview.organization_name : "Organisation confidentielle"}</div>
                  </div>
                ) : <div className="text-xs text-muted animate-pulse">Chargement de l'aperçu…</div>}
                <p className="text-[11px] text-muted mt-3">Enregistrez pour rafraîchir l'aperçu. Les coordonnées privées ne figurent jamais ici.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
